import {useCallback, useMemo} from 'react'
import {useSelector} from 'react-redux'
import {getAnalyticsPageName, getAnonymousId} from 'modules/meta/selectors'
import {getOfferEntity} from 'modules/sapiSearch/selectors'
import {getTopOffers} from 'modules/sapiSearch/utils'
import {getProfileUrlParam} from 'modules/search/selectors'
import {v4 as uuid} from 'uuid'

import {useDeviceLayout} from '@vio/atlas/src/context/deviceLayout'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {useDispatchTeamEvent} from '@vio/core/src/analytics/hooks/useDispatchTeamEvent'
import {
  trackEvent,
  trackProviderRedirect
} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'
import {TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY} from '@vio/core/src/auth/modules/actions'
import {useToggle} from '@vio/core/src/experiments/hooks/useToggle'
import {augmentBookUriWithAnonymousId} from '@vio/core/src/offer/business/offers'
import {SearchOffer} from '@vio/core/src/offer/types/SearchOffer'
import {isMetaProfile} from '@vio/core/src/profile'

import {
  setBackgroundTabTriggered,
  shouldTriggerExpediaBackgroundTabForOffer
} from './helpers'

interface UseBoostExpediaBackgroundTabParams {
  hotelId: string
  offer: SearchOffer
  originalOnClick: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void
}

/**
 * Hook to enhance offer clicks with Boost Expedia background tab redirect experiment
 *
 * Added as part of 2a7f5ff0-expedia-back-tab
 * To remove when cleaning up: Delete this hook call and import from OfferLink
 */
export const useBoostExpediaBackgroundTabOffer = ({
  hotelId,
  offer,
  originalOnClick
}: UseBoostExpediaBackgroundTabParams) => {
  const toggle = useToggle()
  const dispatchTeamEvent = useDispatchTeamEvent()
  const anonymousId = useSelector(getAnonymousId)
  const pageName = useSelector(getAnalyticsPageName)
  const profile = useSelector(getProfileUrlParam)
  const {isMobile} = useDeviceLayout()
  const {search} = useBrandCapabilities()

  const offerEntity = useSelector(state => getOfferEntity(state, hotelId))
  const topOffers = getTopOffers(offerEntity)

  const {shouldTrigger, expOffer} = shouldTriggerExpediaBackgroundTabForOffer({
    clickedOffer: offer,
    topOffers,
    isEligible: isMobile && search.hasExpediaBackTab && isMetaProfile(profile),
    pageName
  })

  // Generate direct EXP offer URL (bypassing provider redirect screen)
  // We add anonymousId for booking attribution tracking
  const expOfferDirectUrl = useMemo(() => {
    if (!expOffer?.url) return ''
    return augmentBookUriWithAnonymousId(anonymousId, expOffer.url)
  }, [expOffer, anonymousId])

  const enhancedOnClick = useCallback(
    (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
      if (shouldTrigger && expOffer) {
        const isExperimentEnabled = toggle(
          '2a7f5ff0-expedia-back-tab',
          false,
          true
        )

        // Set on both variants so A only triggers where B would open the back-tab
        setBackgroundTabTriggered()

        if (isExperimentEnabled) {
          // Fire standard ProviderRedirected event for analytics
          const redirectId = uuid()
          dispatchTeamEvent(
            trackProviderRedirect({
              offerId: expOffer.id,
              intermediaryProvider: expOffer.intermediaryProvider,
              redirectId
            })
          )

          setTimeout(() => {
            window.location.href = expOfferDirectUrl
          }, TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY)
        }

        dispatchTeamEvent(
          trackEvent({
            category: Category.System,
            entity: Entity.ExpediaBackTab,
            action: Action.Triggered,
            page: pageName,
            payload: {
              triggerType: 'offerClick',
              clickedOfferId: offer.id
            }
          })
        )
      }

      originalOnClick(event)
    },
    [
      shouldTrigger,
      expOffer,
      originalOnClick,
      toggle,
      dispatchTeamEvent,
      offer.id,
      expOfferDirectUrl,
      pageName
    ]
  )

  return enhancedOnClick
}
